import {
  PLAYER_COLORS,
  PLAYER_BORDER_COLORS,
  CATEGORY_FILLS,
  CATEGORY_BADGE_COLOR,
  categoryOfCardKey,
} from "@/lib/api";
import { TileArt, TokenArt, TOKEN_KEYS } from "@/components/game/InfraArt";
import { S } from "@/lib/sound";
import { FullStats } from "@/components/game/CardStats";
import { cardTypeLabel } from "@/components/game/CardArt";
import { useEffect, useState } from "react";
import { cardImageSrc, logMissingArt, CARD_ASPECT, CARD_RATIO, isLegacyArt } from "@/lib/artAssets";

// Fallback card (no art): printed type badge, six stats, placement text.
/**
 * Shared card display used in hand & market. Layout follows the
 * design system: art on top ~43%, then name (Fraunces bold), flavour,
 * stat pill row, footer with cost coin + placement note.
 */
function LegacyCard({
  card,
  size = "md",
  selected = false,
  disabled = false,
  onClick,
  ownerColor,
  testid,
}) {
  const isBuild = card.kind === "build";
  const widthClass =
    size === "fit"
      ? "w-full h-full"
      : size === "sm"
        ? "w-[112px]"
        : "w-[164px]";
  const category = categoryOfCardKey(card.key);
  const catFill = CATEGORY_FILLS[category] || CATEGORY_FILLS.gray;
  const catBadge = CATEGORY_BADGE_COLOR[category] || "#7d7870";
  const isTokenPiece = TOKEN_KEYS.includes(card.key)
    && (card.placement?.is_token_on_cube || card.placement?.is_token_on_tile);
  const piece = cardTypeLabel(card);
  const flavour = card.description || "";
  const placementNote = (card.placement_text || "").split("\n")[0];

  // Owner border ring (used on Action cards where the ownership matters)
  const ringColor = ownerColor
    ? PLAYER_BORDER_COLORS[
        Object.keys(PLAYER_COLORS).find((k) => PLAYER_COLORS[k] === ownerColor)
      ] || ownerColor
    : null;

  return (
    <button
      type="button"
      data-testid={testid || `card-${card.key}`}
      disabled={disabled}
      onMouseEnter={() => !disabled && S.cardHover()}
      onClick={onClick}
      className={`pcard ${!disabled ? "pcard--hoverable" : ""} ${
        selected ? "pcard--selected" : ""
      } ${widthClass} flex flex-col text-left`}
      style={{
        padding: 0,
        ...(size === "fit" ? { aspectRatio: "auto" } : {}),
      }}
    >
      {/* ART AREA — top ~43% */}
      <div
        className="relative w-full"
        style={{
          height: "43%",
          padding: 8,
        }}
      >
        <div
          className="relative w-full h-full rounded-[10px] overflow-hidden"
          style={{ background: isBuild ? catFill : "#2b2620" }}
        >
          {isBuild ? (
            isTokenPiece ? (
              <div className="absolute inset-0 flex items-center justify-center p-2">
                <div className="w-3/5 aspect-square rounded-full overflow-hidden">
                  <TokenArt cardKey={card.key} category={category} />
                </div>
              </div>
            ) : (
              <TileArt cardKey={card.key} category={category} />
            )
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className="badge-text"
                style={{ color: "#fdfbf5", opacity: 0.85 }}
              >
                {card.kind}
              </span>
            </div>
          )}

          {/* Category badge — top-left */}
          {isBuild && (
            <span
              className="cat-badge badge-text absolute"
              style={{ top: 6, left: 6, background: catBadge }}
              data-testid={`card-category-${card.key}`}
            >
              {piece}
            </span>
          )}


          {/* Owner ring for non-build cards */}
          {!isBuild && ringColor && (
            <span
              className="absolute inset-0 rounded-[10px] pointer-events-none"
              style={{ boxShadow: `inset 0 0 0 3px ${ringColor}` }}
            />
          )}
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-1 flex flex-col px-3 pb-3">
        <div className="card-name mt-2" data-testid={`card-name-${card.key}`}>
          {card.name}
        </div>

        {flavour && (
          <div className="flavour-text mt-1.5 leading-tight">
            {flavour.length > 90 ? `${flavour.slice(0, 90)}…` : flavour}
          </div>
        )}

        {/* Six stats (build cards only) */}
        {isBuild && (
          <div className="mt-2">
            <FullStats card={card} testid={`card-stats-${card.key}`} dense />
          </div>
        )}

        {/* Footer — cost coin + placement note */}
        <div
          className="mt-auto flex items-center gap-2 pt-2"
          style={{ borderTop: "1px solid #e6ddc8" }}
        >
          {isBuild ? (
            <div
              className="coin"
              style={{ width: 30, height: 30 }}
              data-testid={`card-cost-${card.key}`}
              title={`Cost ${card.cost}`}
            >
              {card.cost}
            </div>
          ) : (
            ownerColor && (
              <div
                className="rounded-full"
                style={{
                  width: 22,
                  height: 22,
                  background: ownerColor,
                  border: `1px solid ${ringColor || "var(--line)"}`,
                }}
                aria-label="owner"
              />
            )
          )}
          <div
            className="placement-note leading-tight flex-1"
            style={{ color: "var(--ink-soft)" }}
          >
            {placementNote ||
              (isBuild ? "Place on empty city cell" : "Play from hand")}
          </div>
        </div>
      </div>
    </button>
  );
}

// Card face = the printed artwork only; interactions are overlays. Falls back to LegacyCard.
export default function GameCard(props) {
  const { card, size = "md", selected = false, disabled = false, unaffordable = false, onClick, testid } = props;
  const src = cardImageSrc(card.key);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!src) logMissingArt(`card_${card.key}.png`);
  }, [src, card.key]);
  if (!src || failed) return <LegacyCard {...props} />;

  const widthClass = size === "sm" ? "w-[112px]" : size === "md" ? "w-[164px]" : "";
  const face = (
    <button
      type="button"
      data-testid={testid || `card-${card.key}`}
      data-art="image"
      data-unaffordable={unaffordable ? "true" : undefined}
      disabled={disabled}
      onMouseEnter={() => !disabled && S.cardHover()}
      onClick={onClick}
      title={card.name}
      aria-label={card.name}
      className={`pcard-art ${isLegacyArt(card.key) ? "pcard-art--legacy" : ""} ${widthClass} ${!disabled ? "pcard-art--hoverable" : ""} ${selected ? "pcard-art--selected" : ""} ${unaffordable ? "pcard-art--dim" : ""}`}
      style={{ aspectRatio: CARD_ASPECT, ...(size === "fit" ? { width: `min(100cqw, calc(100cqh / ${CARD_RATIO}))` } : {}) }}
    >
      <img decoding="async"
        src={src}
        alt={card.name}
        draggable={false}
        decoding="async"
        onError={() => {
          logMissingArt(src);
          setFailed(true);
        }}
      />
    </button>
  );
  if (size !== "fit") return face;
  return (
    <div className="w-full h-full flex items-center justify-center" style={{ containerType: "size" }}>
      {face}
    </div>
  );
}

// re-export for consumers that want the color map
export { PLAYER_COLORS };
